import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, CheckCircle2, Circle, FileSignature, BadgeCheck, CarFront, Award, Inbox } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { trackApplication, formatApiErrorDetail } from "@/api";

const buildSteps = (r) => [
  { key: "received", icon: Inbox, label: "Application received", done: true, date: r.created_at },
  {
    key: "review", icon: Search, label: "Verification in progress",
    done: ["contacted", "in-progress", "agreement-sent", "agreement-signed", "closed"].includes(r.status) || Boolean(r.agreement_sent_at),
    date: null,
  },
  { key: "agreement", icon: FileSignature, label: "Agreement ready for signature", done: Boolean(r.agreement_sent_at) || Boolean(r.agreement_signed_at), date: r.agreement_sent_at },
  { key: "signed", icon: BadgeCheck, label: "Agreement signed — plan active", done: Boolean(r.agreement_signed_at), date: r.agreement_signed_at },
  { key: "settled", icon: Award, label: r.certificate_issued ? "Ownership complete — certificate issued" : "Ownership complete", done: r.settled, date: null },
];

export default function Track() {
  const [form, setForm] = useState({ reference: "", id_number: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      setResult(await trackApplication(form));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="track-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Application tracker</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-4">
            Where is my application?
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mb-10">
            Enter your application reference (e.g. CCM-AB12CD) and the ID number you applied with.
            We'll show you exactly where things stand — no phone call needed.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="track-form" className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 space-y-5 mb-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="input-label" htmlFor="track-ref">Application reference</label>
                <input id="track-ref" data-testid="track-ref-input" required className="input-field uppercase" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="CCM-XXXXXX" />
              </div>
              <div>
                <label className="input-label" htmlFor="track-id">ID / passport number</label>
                <input id="track-id" data-testid="track-id-input" required className="input-field" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} placeholder="9001014800082" />
              </div>
            </div>
            <button type="submit" data-testid="track-submit-btn" disabled={loading} className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              {loading ? "Checking…" : "Track my application"}
            </button>
            {error && <p data-testid="track-error" className="text-sm text-red-400 text-center">{error}</p>}
          </form>
        </Reveal>

        <AnimatePresence>
          {result && (
            <motion.div
              key="result"
              data-testid="track-result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#122659] border border-sky-300/20 rounded-xl p-8"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-8 pb-6 border-b border-sky-300/10">
                <div>
                  <p className="text-xs uppercase tracking-widest text-sky-300 font-bold">Reference {result.reference}</p>
                  <p className="font-display text-2xl font-black text-white mt-1" data-testid="track-greeting">Hi {result.first_name}</p>
                </div>
                {result.vehicle && (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-300 border border-sky-300/30 rounded-full px-4 py-2" data-testid="track-vehicle">
                    <CarFront size={16} className="text-[#F57C00]" /> {result.vehicle}
                  </span>
                )}
              </div>
              <ol className="space-y-0" data-testid="track-timeline">
                {buildSteps(result).map((s, i) => (
                  <li key={s.key} data-testid={`track-step-${s.key}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      {s.done ? (
                        <CheckCircle2 size={24} className="text-emerald-400 shrink-0" />
                      ) : (
                        <Circle size={24} className="text-slate-600 shrink-0" />
                      )}
                      {i < 4 && <div className={`w-px flex-1 min-h-8 ${s.done ? "bg-emerald-400/50" : "bg-slate-700"}`} />}
                    </div>
                    <div className="pb-8">
                      <p className={`text-sm font-bold ${s.done ? "text-white" : "text-slate-500"}`}>{s.label}</p>
                      {s.done && s.date && (
                        <p className="text-xs text-slate-500 mt-1">{new Date(s.date).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              <p className="text-[11px] text-slate-500 border-t border-sky-300/10 pt-4">
                Questions about your application? Call 010 534 9926 (Mon–Fri 08:00–17:00, Sat 08:00–13:00).
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
